import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from "react";
import { Link, type LinkProps } from "react-router-dom";

type ButtonVariant = "primary" | "secondary" | "danger";
type ButtonSize = "sm" | "md";

type ButtonBaseProps = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
};

type ButtonAsButton = ButtonBaseProps &
  Omit<ComponentPropsWithoutRef<"button">, keyof ButtonBaseProps> & {
    as?: "button";
  };

type ButtonAsLink = ButtonBaseProps &
  Omit<LinkProps, keyof ButtonBaseProps> & {
    as: typeof Link;
  };

type ButtonProps = ButtonAsButton | ButtonAsLink;

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    "bg-primary-500 text-white hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-500",
  secondary:
    "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-dark-bg-tertiary dark:text-dark-text-secondary dark:hover:bg-dark-border",
  danger: "bg-red-500 text-white hover:bg-red-600",
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: "px-3 py-1.5 text-sm",
  md: "px-4 py-2",
};

const baseStyles =
  "rounded-lg font-medium transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed";

function isLinkProps(props: ButtonProps): props is ButtonAsLink {
  return props.as === Link;
}

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>((props, ref) => {
  const { variant = "primary", size = "md", children, className = "", ...rest } = props;

  const combinedClassName =
    `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`.trim();

  if (isLinkProps(props)) {
    const { as: _as, ...linkProps } = rest as Omit<ButtonAsLink, "variant" | "size" | "children">;
    return (
      <Link
        ref={ref as React.Ref<HTMLAnchorElement>}
        className={combinedClassName}
        {...(linkProps as Omit<LinkProps, "className">)}
      >
        {children}
      </Link>
    );
  }

  const { as: _as, ...buttonProps } = rest as Omit<ButtonAsButton, "variant" | "size" | "children">;
  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      type="button"
      className={combinedClassName}
      {...(buttonProps as Omit<ComponentPropsWithoutRef<"button">, "className">)}
    >
      {children}
    </button>
  );
});

Button.displayName = "Button";

export default Button;
